'use client';

import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { usePrivy } from '@privy-io/react-auth';

import { useAuth } from './useAuth';
import { useOpenAuthModal } from './useOpenAuthModal';

/**
 * Routes a signed-out (guest) user is allowed to view. Everything else redirects
 * to the landing page and opens the login modal.
 *
 * Terms / Privacy / Contact stay public because they are linked from the signup
 * modal footer (and are typically legally required to be reachable) — gating them
 * would trap a user who opens them mid-signup.
 */
export const GUEST_ALLOWED_PATHS = ['/', '/terms', '/privacy', '/contact-us'] as const;

export const isGuestAllowedPath = (pathname: string | null): boolean => {
  if (!pathname) return true;
  return GUEST_ALLOWED_PATHS.some(
    (p) => pathname === p || (p !== '/' && pathname.startsWith(`${p}/`))
  );
};

/**
 * Access gate for signed-out users: they may only view the landing page (and the
 * few public pages above). This hook is **pure** — it only computes state and
 * hands back a `guardGuestAction` helper; the actual redirect side-effect lives in
 * `MainLayout` so it runs exactly once.
 */
export const useGuestGate = () => {
  const pathname = usePathname();
  const { ready, authenticated } = usePrivy();
  const { isAuthenticated } = useAuth();
  const openAuthModal = useOpenAuthModal();

  // "Definitive guest": Privy has initialised and there is no session at all.
  // Keying on Privy's `authenticated` (not just our backend flag) avoids bouncing
  // a real user during the Privy → backend-login window, where our own
  // `isAuthenticated` is briefly still false.
  const isGuest = ready && !authenticated && !isAuthenticated;

  const isBlockedRoute = isGuest && !isGuestAllowedPath(pathname);

  /**
   * Intercept an interactive guest action (click / navigation). Returns `true`
   * when it handled the event by opening the login modal, `false` when the caller
   * should let the action proceed (i.e. the user is signed in).
   */
  const guardGuestAction = useCallback(
    (e?: { preventDefault?: () => void; stopPropagation?: () => void }): boolean => {
      if (!isGuest) return false;
      e?.preventDefault?.();
      e?.stopPropagation?.();
      openAuthModal();
      return true;
    },
    [isGuest, openAuthModal]
  );

  return { isGuest, isBlockedRoute, guardGuestAction };
};

export default useGuestGate;
